import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable";
import TooltipOption from "../../../core/common/tooltipOption";
import { all_routes } from "../../router/all_routes";
import { SORT_OPTIONS, statusOptions } from "../../../core/common/selectoption/selectoption";
import {
  TransportShift as TransportShiftType,
  TransportShiftStatus,
  createTransportShift,
  getAllTransportShifts,
  updateTransportShift,
} from "../../../service/transportShift";
import {
  printTable,
  exportTableToPdf,
  exportTableToExcel,
} from "../../../utils/exportTable";
import { toast } from "react-toastify";

type TransportShiftRow = TransportShiftType & {
  statusClass: string;
  identifier?: string;
};

const EXPORT_HEADERS = ["ID", "Name", "Status"];

const toExportRows = (rows: TransportShiftRow[]): string[][] =>
  rows.map((s) => [s.identifier || s.id || "", s.name, s.status]);

const mapItem = (item: any): TransportShiftRow => ({
  key: item._id,
  id: item._id,
  identifier: item.transportShiftId || item.identifier || item.shiftId,
  name: item.name,
  status: item.status as TransportShiftStatus,
  createdAt: item.createdAt,
  updatedAt: item.updatedAt,
  statusClass: item.status === "active" ? "badge-soft-success" : "badge-soft-danger",
});

const TransportShift: React.FC = () => {
  const routes = all_routes;
  const currentBranch = useSelector(selectCurrentBranch);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const [data, setData] = useState<TransportShiftRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState<string>("");
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [filters, setFilters] = useState<{ status?: Option }>({});
  const [appliedFilters, setAppliedFilters] = useState<{ status?: string }>({});
  const [shiftToEdit, setShiftToEdit] = useState<TransportShiftRow | null>(null);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [printLoading, setPrintLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const [addForm, setAddForm] = useState({ name: "" });
  const [editForm, setEditForm] = useState({
    name: "",
    status: "active" as TransportShiftStatus,
  });

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllTransportShifts({
        searchText,
        status: appliedFilters.status || "",
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
      });

      const mapped = response.data.map(mapItem);
      setData(mapped);
      setTotalCount(response.pagination?.total || mapped.length);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to fetch transport shifts"
      );
    } finally {
      setLoading(false);
    }
  }, [searchText, appliedFilters.status, sortBy.value.sortBy, sortBy.value.sortOrder]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  const handleApplyFilters = () => {
    if (dropdownMenuRef.current) dropdownMenuRef.current.classList.remove("show");
    setAppliedFilters({ status: filters.status?.value });
  };

  const handleResetFilters = () => {
    setFilters({});
    setAppliedFilters({});
    if (dropdownMenuRef.current) dropdownMenuRef.current.classList.remove("show");
  };

  const handleSortSelect = (item: (typeof SORT_OPTIONS)[0]) => setSortBy(item);

  const handleAddShift = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddSubmitting(true);
    try {
      const payload = { name: addForm.name.trim(), status: "active" as TransportShiftStatus };
      const response = await createTransportShift(payload);

      if (response.success || response._id || response.id || response.status === "success") {
        toast.success("Transport shift added successfully!");
        setAddForm({ name: "" });
        fetchShifts();
        const el = document.getElementById("add_transport_shift");
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal");
          el.click();
          el.removeAttribute("data-bs-dismiss");
        }
      } else {
        toast.error(response.message || response.error || "Failed to add shift");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to add transport shift"
      );
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEditModal = (shift: TransportShiftRow) => {
    setShiftToEdit(shift);
    setEditForm({ name: shift.name, status: shift.status });
  };

  const handleEditShift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftToEdit) return;
    try {
      const payload = { name: editForm.name.trim(), status: editForm.status };
      const response = await updateTransportShift(shiftToEdit.id, payload);

      if (response.success || response._id || response.id || response.status === "success") {
        toast.success("Transport shift updated successfully!");
        setShiftToEdit(null);
        fetchShifts();
        const el = document.getElementById("edit_transport_shift");
        if (el) {
          el.setAttribute("data-bs-dismiss", "modal");
          el.click();
          el.removeAttribute("data-bs-dismiss");
        }
      } else {
        toast.error(response.message || response.error || "Failed to update shift");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to update transport shift"
      );
    }
  };

  const fetchAllForExport = async (): Promise<TransportShiftRow[]> => {
    const response = await getAllTransportShifts({
      searchText,
      status: appliedFilters.status || "",
      sortBy: sortBy.value.sortBy,
      sortOrder: sortBy.value.sortOrder,
      limit: totalCount || 500,
      page: 1,
    });
    return response.data.map(mapItem);
  };

  const handlePrint = async () => {
    if (!totalCount) { toast.warning("No data to print"); return; }
    setPrintLoading(true);
    try {
      const all = await fetchAllForExport();
      printTable("Transport Shifts", EXPORT_HEADERS, toExportRows(all));
    } catch {
      toast.error("Failed to prepare print data");
    } finally {
      setPrintLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!totalCount) { toast.warning("No data to export"); return; }
    setPdfLoading(true);
    try {
      const all = await fetchAllForExport();
      exportTableToPdf("Transport Shifts", EXPORT_HEADERS, toExportRows(all), "transport-shifts.pdf");
    } catch {
      toast.error("Failed to prepare PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!totalCount) { toast.warning("No data to export"); return; }
    setExcelLoading(true);
    try {
      const all = await fetchAllForExport();
      exportTableToExcel(
        all.map((s) => ({ ID: s.identifier || s.id, Name: s.name, Status: s.status })),
        "Transport Shifts",
        "transport-shifts.xlsx"
      );
    } catch {
      toast.error("Failed to prepare Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "identifier",
      render: (identifier: string) => identifier || "-",
      sorter: (a: TransportShiftRow, b: TransportShiftRow) =>
        (a.identifier || "").localeCompare(b.identifier || ""),
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: TransportShiftRow, b: TransportShiftRow) => a.name.localeCompare(b.name),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: TransportShiftStatus) => (
        <span
          className={`badge ${
            status === "active" ? "badge-soft-success" : "badge-soft-danger"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {status === "active" ? "Active" : "Inactive"}
        </span>
      ),
      sorter: (a: TransportShiftRow, b: TransportShiftRow) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: TransportShiftRow) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_transport_shift"
                  onClick={() => openEditModal(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Transport Shift</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Transport Shift
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchShifts}
                onPrint={handlePrint}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                printLoading={printLoading}
                exportPdfLoading={pdfLoading}
                exportExcelLoading={excelLoading}
              />
              <div className="mb-2">
                <Link to="#" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#add_transport_shift">
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Shift
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Transport Shifts List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" /> Filter
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className="select"
                            options={statusOptions}
                            value={filters.status}
                            onChange={(val) => setFilters({ status: val as Option })}
                          />
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <div className="btn btn-light me-3" onClick={handleResetFilters}>Reset</div>
                        <div className="btn btn-primary" onClick={handleApplyFilters}>Apply</div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <Table setSearchText={setSearchText} dataSource={data} columns={columns} loading={loading} />
            </div>
          </div>
        </div>
      </div>

      {/* Add Transport Shift Modal */}
      <div className="modal fade" id="add_transport_shift" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content rounded-3 border-0 shadow-sm">
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h5 className="modal-title fw-semibold text-dark">Add Transport Shift</h5>
              <button type="button" className="btn-close bg-light rounded-circle p-2 opacity-75" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <form onSubmit={handleAddShift}>
              <div className="modal-body px-4 pt-3 pb-4">
                <div className="mb-3">
                  <label className="form-label text-dark fw-medium mb-1">
                    Shift Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-2 border border-secondary border-opacity-25"
                    placeholder="Enter shift name"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-light rounded-2 border border-secondary border-opacity-25 text-dark px-3" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary rounded-2 px-4" disabled={addSubmitting}>
                  {addSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Adding...
                    </>
                  ) : (
                    "Add Shift"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Transport Shift Modal */}
      <div className="modal fade" id="edit_transport_shift" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content rounded-3 border-0 shadow-sm">
            <div className="modal-header border-0 pb-0 pt-4 px-4">
              <h5 className="modal-title fw-semibold text-dark">Edit Transport Shift</h5>
              <button type="button" className="btn-close bg-light rounded-circle p-2 opacity-75" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <form onSubmit={handleEditShift}>
              <div className="modal-body px-4 pt-3 pb-4">
                <div className="mb-3">
                  <label className="form-label text-dark fw-medium mb-1">
                    Shift Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-2 border border-secondary border-opacity-25"
                    placeholder="Enter shift name"
                    value={editForm.name}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="pt-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <span className="form-label text-dark fw-medium mb-0 d-block">Status</span>
                      <p className="text-muted small mb-0 mt-0">Change the Status by toggle</p>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="edit_transport_shift_status"
                        checked={editForm.status === "active"}
                        onChange={(e) =>
                          setEditForm((prev) => ({
                            ...prev,
                            status: e.target.checked ? "active" : "inactive",
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 pb-4 px-4 d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-light rounded-2 border border-secondary border-opacity-25 text-dark px-3" data-bs-dismiss="modal">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary rounded-2 px-4">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransportShift;
